import { BirdviewSection } from '../DashboardBirdview.constants'
import {
  BIRDVIEW_AGING_BASIS_OPTIONS,
  BIRDVIEW_CHART_BUILDING_METRIC_OPTIONS,
  BIRDVIEW_CHART_MANAGER_METRIC_OPTIONS,
  BirdviewAnalyticsPreset,
  BirdviewChartObject,
  BirdviewChartTone,
  BirdviewChartType,
  BirdviewKpiUnit,
  BirdviewRequestChart,
} from '../DashboardBirdview.constants.charts'

export const BirdviewChartTitlePrefix = {
  Requests: 'requests',
  Scope: 'scope',
}

// The stored layout is keyed by these values, so renaming one resets it.
const BirdviewChartId = {
  BuildingsByCity: 'buildingsByCity',
  ContractsEnding: 'contractsEnding',
  DocFlowsByPhase: 'docFlowsByPhase',
  EmailsOverTime: 'emailsOverTime',
  OpenedVsClosed: 'openedVsClosed',
  RequestsAging: 'requestsAging',
  RequestsByBuilding: 'requestsByBuilding',
  RequestsByManager: 'requestsByManager',
  RequestsByProvider: 'requestsByProvider',
  RequestsByStatus: 'requestsByStatus',
  RequestsByType: 'requestsByType',
  UnansweredEmailsByAge: 'unansweredEmailsByAge',
  UnitsOccupancy: 'unitsOccupancy',
}

const BIRDVIEW_FLOW_CHART = {
  columnSpan: 2,
  dataKey: BirdviewRequestChart.OpenedVsClosed,
  id: BirdviewChartId.OpenedVsClosed,
  object: BirdviewChartObject.Requests,
  questionKey: 'DashboardBirdview.ChartOpenedVsClosedQuestionKey',
  sectionKey: BirdviewSection.ChartRequests,
  titleKey: 'DashboardBirdview.ChartOpenedVsClosedTitleKey',
  titlePrefix: BirdviewChartTitlePrefix.Scope,
  type: BirdviewChartType.Line,
}

const BIRDVIEW_STATUS_CHART = {
  dataKey: BirdviewRequestChart.ByStatus,
  id: BirdviewChartId.RequestsByStatus,
  object: BirdviewChartObject.Requests,
  questionKey: 'DashboardBirdview.ChartRequestsByStatusQuestionKey',
  sectionKey: BirdviewSection.ChartRequests,
  titleKey: 'DashboardBirdview.ChartRequestsByStatusTitleKey',
  titlePrefix: BirdviewChartTitlePrefix.Scope,
  type: BirdviewChartType.Donut,
}

// Aging counts the whole portfolio, so its title carries no My / Team scope.
const BIRDVIEW_AGING_CHART = {
  dataKey: BirdviewRequestChart.Aging,
  id: BirdviewChartId.RequestsAging,
  metricOptions: BIRDVIEW_AGING_BASIS_OPTIONS,
  object: BirdviewChartObject.Requests,
  questionKey: 'DashboardBirdview.ChartAgingSlaQuestionKey',
  sectionKey: BirdviewSection.ChartRequests,
  titleKey: 'DashboardBirdview.ChartAgingSlaTitleKey',
  titlePrefix: BirdviewChartTitlePrefix.Requests,
  type: BirdviewChartType.Aging,
}

const BIRDVIEW_BUILDING_CHART = {
  color: BirdviewChartTone.Success,
  columnSpan: 2,
  dataKey: BirdviewRequestChart.ByBuilding,
  id: BirdviewChartId.RequestsByBuilding,
  metricOptions: BIRDVIEW_CHART_BUILDING_METRIC_OPTIONS,
  object: BirdviewChartObject.Requests,
  questionKey: 'DashboardBirdview.ChartRequestsByBuildingQuestionKey',
  sectionKey: BirdviewSection.ChartRequests,
  titleKey: 'DashboardBirdview.ChartRequestsByBuildingTitleKey',
  titlePrefix: BirdviewChartTitlePrefix.Scope,
  type: BirdviewChartType.Bars,
}

const BIRDVIEW_CATEGORY_CHART = {
  color: BirdviewChartTone.Info,
  dataKey: BirdviewRequestChart.ByType,
  id: BirdviewChartId.RequestsByType,
  object: BirdviewChartObject.Requests,
  questionKey: 'DashboardBirdview.ChartRequestsByTypeQuestionKey',
  sectionKey: BirdviewSection.ChartRequests,
  titleKey: 'DashboardBirdview.ChartRequestsByTypeTitleKey',
  titlePrefix: BirdviewChartTitlePrefix.Scope,
  type: BirdviewChartType.Bars,
}

const BIRDVIEW_EMAILS_OVER_TIME_CHART = {
  id: BirdviewChartId.EmailsOverTime,
  object: BirdviewChartObject.Emails,
  questionKey: 'DashboardBirdview.ChartEmailsOverTimeQuestionKey',
  titleKey: 'DashboardBirdview.ChartEmailsOverTimeTitleKey',
}

const BIRDVIEW_UNANSWERED_EMAILS_CHART = {
  id: BirdviewChartId.UnansweredEmailsByAge,
  object: BirdviewChartObject.Emails,
  questionKey: 'DashboardBirdview.ChartUnansweredEmailsByAgeQuestionKey',
  titleKey: 'DashboardBirdview.ChartUnansweredEmailsByAgeTitleKey',
}

// The KPI set per preset is fixed; the section only brings the numbers.
export const BIRDVIEW_KPI_DEFINITIONS = {
  [BirdviewAnalyticsPreset.Personal]: [
    {
      id: 'resolvedThisWeek',
      labelKey: 'DashboardBirdview.KpiResolvedThisWeekKey',
      unit: BirdviewKpiUnit.Count,
    },
    {
      id: 'myAvgResolution',
      labelKey: 'DashboardBirdview.KpiMyAvgResolutionKey',
      unit: BirdviewKpiUnit.Days,
    },
    {
      id: 'onTime',
      labelKey: 'DashboardBirdview.KpiOnTimeKey',
      unit: BirdviewKpiUnit.Percent,
    },
  ],
  [BirdviewAnalyticsPreset.Portfolio]: [
    {
      id: 'avgResolution',
      labelKey: 'DashboardBirdview.KpiAvgResolutionKey',
      unit: BirdviewKpiUnit.Days,
    },
    {
      id: 'onTime',
      labelKey: 'DashboardBirdview.KpiOnTimeKey',
      unit: BirdviewKpiUnit.Percent,
    },
    {
      deltaUnit: BirdviewKpiUnit.Percent,
      id: 'backlog',
      labelKey: 'DashboardBirdview.TileBacklogKey',
      unit: BirdviewKpiUnit.Count,
    },
  ],
}

// A two-column lead chart makes the four-column grid read as three per row.
export const BIRDVIEW_ANALYTICS_CHARTS = {
  [BirdviewAnalyticsPreset.Personal]: [
    BIRDVIEW_FLOW_CHART,
    BIRDVIEW_STATUS_CHART,
    BIRDVIEW_AGING_CHART,
    BIRDVIEW_BUILDING_CHART,
    // Five charts leave the last row half empty, so by category fills it.
    { ...BIRDVIEW_CATEGORY_CHART, columnSpan: 2 },
  ],
  [BirdviewAnalyticsPreset.Portfolio]: [
    BIRDVIEW_FLOW_CHART,
    BIRDVIEW_STATUS_CHART,
    BIRDVIEW_AGING_CHART,
    BIRDVIEW_BUILDING_CHART,
    BIRDVIEW_CATEGORY_CHART,
    {
      color: BirdviewChartTone.Info,
      dataKey: BirdviewRequestChart.ByManager,
      id: BirdviewChartId.RequestsByManager,
      isStacked: true,
      metricOptions: BIRDVIEW_CHART_MANAGER_METRIC_OPTIONS,
      noteKey: 'DashboardBirdview.ChartByManagerCountingNoteKey',
      object: BirdviewChartObject.Requests,
      questionKey: 'DashboardBirdview.ChartRequestsByManagerQuestionKey',
      sectionKey: BirdviewSection.ChartRequests,
      titleKey: 'DashboardBirdview.ChartRequestsByManagerTitleKey',
      titlePrefix: BirdviewChartTitlePrefix.Requests,
      type: BirdviewChartType.Bars,
      withOwnTotal: true,
    },
  ],
}

// Part 2 of the catalogue: listed ahead of their data, so they cannot be added.
export const BIRDVIEW_ANALYTICS_SOON_CHARTS = {
  [BirdviewAnalyticsPreset.Personal]: [
    BIRDVIEW_EMAILS_OVER_TIME_CHART,
    BIRDVIEW_UNANSWERED_EMAILS_CHART,
  ],
  [BirdviewAnalyticsPreset.Portfolio]: [
    {
      id: BirdviewChartId.RequestsByProvider,
      object: BirdviewChartObject.Requests,
      questionKey: 'DashboardBirdview.ChartRequestsByProviderQuestionKey',
      titleKey: 'DashboardBirdview.ChartRequestsByProviderTitleKey',
    },
    {
      id: BirdviewChartId.BuildingsByCity,
      object: BirdviewChartObject.Buildings,
      questionKey: 'DashboardBirdview.ChartBuildingsByCityQuestionKey',
      titleKey: 'DashboardBirdview.ChartBuildingsByCityTitleKey',
    },
    {
      id: BirdviewChartId.UnitsOccupancy,
      object: BirdviewChartObject.Units,
      questionKey: 'DashboardBirdview.ChartUnitsOccupancyQuestionKey',
      titleKey: 'DashboardBirdview.ChartUnitsOccupancyTitleKey',
    },
    {
      id: BirdviewChartId.ContractsEnding,
      object: BirdviewChartObject.Units,
      questionKey: 'DashboardBirdview.ChartContractsEndingQuestionKey',
      titleKey: 'DashboardBirdview.ChartContractsEndingTitleKey',
    },
    BIRDVIEW_EMAILS_OVER_TIME_CHART,
    BIRDVIEW_UNANSWERED_EMAILS_CHART,
    {
      id: BirdviewChartId.DocFlowsByPhase,
      object: BirdviewChartObject.DocFlows,
      questionKey: 'DashboardBirdview.ChartDocFlowsByPhaseQuestionKey',
      titleKey: 'DashboardBirdview.ChartDocFlowsByPhaseTitleKey',
    },
  ],
}

export const BIRDVIEW_ANALYTICS_TILES_PER_ROW = 3
